import { useEffect } from 'react';
import Card from '@material-ui/core/Card';
import List from '@material-ui/core/List';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import BusinessIcon from '@material-ui/icons/Business';
import AppsIcon from '@material-ui/icons/Apps';
import {
  EntityNamespaceFilter,
  EntityUserFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';

// Styling matches the UserListPicker so both sections read as one filter column.
const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'rgba(0, 0, 0, .11)',
    boxShadow: 'none',
    margin: theme.spacing(1, 0, 1, 0),
  },
  title: {
    margin: theme.spacing(1, 0, 0, 1),
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 'bold',
  },
  listIcon: {
    minWidth: 30,
    color: theme.palette.text.primary,
  },
  menuItem: {
    minHeight: theme.spacing(6),
  },
  groupWrapper: {
    margin: theme.spacing(1, 1, 2, 1),
  },
}));

type NamespaceOption = {
  id: string;
  label: string;
  icon: typeof AppsIcon;
  namespaces?: string[];
};

const options: NamespaceOption[] = [
  { id: 'cbws', label: 'CBWS', icon: CloudQueueIcon, namespaces: ['cbws'] },
  {
    id: 'cloudbear',
    label: 'Cloudbear',
    icon: BusinessIcon,
    namespaces: ['cloudbear'],
  },
  { id: 'all', label: 'Everything', icon: AppsIcon },
];

export const NamespaceQuickPicker = () => {
  const classes = useStyles();
  const {
    filters,
    updateFilters,
    queryParameters: { namespace: namespaceParameter },
  } = useEntityList();

  useEffect(() => {
    if (namespaceParameter) {
      updateFilters({
        namespace: new EntityNamespaceFilter([namespaceParameter].flat()),
      });
    }
    // Applies a namespace from the URL once on mount, like the stock pickers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = filters.namespace?.values ?? [];
  const isSelected = (namespaces?: string[]) =>
    namespaces
      ? selected.length === namespaces.length &&
        namespaces.every(namespace => selected.includes(namespace))
      : selected.length === 0;

  return (
    <Card className={classes.root}>
      <div className={classes.groupWrapper}>
        <Typography
          variant="subtitle2"
          component="span"
          className={classes.title}
        >
          Platform
        </Typography>
        <Card>
          <List disablePadding dense role="menu">
            {options.map(option => (
              <MenuItem
                key={option.id}
                divider
                role="menuitem"
                selected={isSelected(option.namespaces)}
                className={classes.menuItem}
                onClick={() =>
                  updateFilters(
                    option.namespaces
                      ? { namespace: new EntityNamespaceFilter(option.namespaces) }
                      : // "Everything" resets both the namespace filter and any
                        // Owned/Starred selection in the personal section.
                        { namespace: undefined, user: EntityUserFilter.all() },
                  )
                }
              >
                <ListItemIcon className={classes.listIcon}>
                  <option.icon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body1">{option.label}</Typography>
                </ListItemText>
              </MenuItem>
            ))}
          </List>
        </Card>
      </div>
    </Card>
  );
};
